import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { IconAlertTriangle, IconBell, IconCalendar, IconCheck, IconChecklist, IconChevronLeft, IconDownload, IconExternalLink, IconFilter, IconLock, IconLockOpen, IconPencil, IconStopwatch, IconUserCheck } from '@tabler/icons-react';
import { Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Drawer, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './settle.i18n';
import { financeScreenState, useFinanceAccess } from './access';
import { SETTLE_FEES, SETTLE_GROUPS, SETTLE_PEOPLE, SETTLE_REC, closeConditions, filterSettle, lineFlags, settleCalc, tr, type SettleFilter, type SettleGroup } from './settleData';
import s from './settle.module.css';
import q from './settlePad.module.css';

// Mint-Settle (1536): quyết toán tạm ứng theo người — kỳ · lọc · bộ phận · 6 số tổng · bảng 7 cột | Đối soát trước khoá kỳ. Tab "Khoá kỳ" = điều kiện chặn.
// TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

type View = 'list' | 'close';
const NAT_TIP: Record<string, string> = { OBH: 'Chi hộ · chứng từ tên khách, khách chịu', BIL: 'Bán lại cho khách · khách chịu', ABS: 'HANA chịu', EXC: 'Bất thường · chứng từ tên khách, HANA chịu' };
const DOC_TIP: Record<string, string> = { INV: 'Hoá đơn', PND: 'Chờ hoá đơn', RCP: 'Biên lai / bảng kê', NDC: 'Không chứng từ' };
const SLIP_ST = { ok: ['Đã duyệt', s.st_ok], wait: ['Chờ duyệt', s.st_wait], rej: ['Bị từ chối', s.st_rej] } as const;
const jobTo = (job: string) => (/^TRF/.test(job) ? null : job);
const FILTERS: readonly (readonly [SettleFilter, string])[] = [['all', 'Tất cả'], ['over', 'Quá hạn'], ['wait', 'Chờ phiếu'], ['due', 'Hoàn / nộp']];
const initials = (u: string) => u.replace(/[^A-ZÀ-Ỹ]/g, '').slice(0, 2);
const HUE = ['var(--av1)', 'var(--av2)', 'var(--av3)', 'var(--av4)', 'var(--av5)', 'var(--av6)'];
const hue = (u: string) => { let h = 0; for (const c of u) h = (h * 31 + c.charCodeAt(0)) >>> 0; return HUE[h % HUE.length]; };
/** Ảnh đại diện theo board: đầu + vai, màu áo theo hash tên; chưa có ảnh → chữ cái đầu. */
function Avatar({ u, photo, cls }: { u: string; photo: boolean; cls?: string }) {
  if (!photo) return <span className={`${cls ?? ""} ${s.avi}`} aria-hidden="true">{initials(u)}</span>;
  return (
    <span className={`${cls ?? ""} ${s.avp}`} title={u} aria-hidden="true">
      <svg width="30" height="30" viewBox="0 0 40 40" style={{ display: 'block' }}>
        <circle cx="20" cy="16" r="7.5" fill="var(--av-skin)" />
        <path d="M5 41c1.2-9.5 7.4-13.5 15-13.5S33.8 31.5 35 41z" fill={hue(u)} />
      </svg>
    </span>
  );
}

export function Settle() {
  const toast = useToast();
  const tx = useT();
  const pad = useLayout() === 'pad';
  const access = useFinanceAccess();
  const [view, setView] = useState<View>('list');
  const [fil, setFil] = useState<SettleFilter>('all');
  const [grp, setGrp] = useState<SettleGroup | 'all'>('all');
  const [per, setPer] = useState<'m10' | 'm09'>('m10');
  const [who, setWho] = useState<string | null>(null);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const cur = SETTLE_PEOPLE.find((p) => p.u === who) ?? null;
  const cc = cur ? settleCalc(cur) : null;
  const dwKpi: readonly (readonly [string, string, string | undefined])[] = cur && cc ? [
    ['Đang giữ', tr(cur.hold), ''], ['Đã chi', tr(cc.ok), ''], ['Chờ duyệt', tr(cc.wait), cc.wait ? s.warn : ''], ['Bị từ chối', tr(cc.rej), cc.rej ? s.err : ''],
    [cc.back ? 'Hoàn ứng' : 'Nộp lại', tr(cc.back || cc.ret), cc.back ? s.link : cc.ret ? s.warn : s.okc], ['Hạn', cur.over ? `−${cur.over} ngày` : cur.due, cur.over ? s.err : ''],
  ] : [];
  const state = financeScreenState(access);
  const base = SETTLE_PEOPLE.filter((p) => grp === 'all' || p.grp === grp);
  const rows = filterSettle(grp, fil);
  const calcs = base.map(settleCalc);
  const sumOf = (k: 'wait' | 'net' | 'back' | 'ret') => calcs.reduce((a, c) => a + c[k], 0);
  const nOver = base.filter((p) => p.over > 0).length;
  const kpis = [
    ['Đang giữ', tr(base.reduce((a, p) => a + p.hold, 0)), '', 'Tạm ứng nhân viên đang giữ · TK 141'],
    ['Phiếu EMP chờ', `${calcs.reduce((a, c) => a + c.nWait, 0)} · ${tr(sumOf('wait'))}`, s.warn, 'Phiếu nhân viên trả chờ duyệt'],
    ['Cấn trừ', tr(sumOf('net')), '', 'Trừ vào tạm ứng đang giữ'],
    ['Hoàn ứng', tr(sumOf('back')), s.link, 'Công ty trả thêm cho nhân viên'],
    ['Nộp lại', tr(sumOf('ret')), s.warn, 'Nhân viên nộp lại phần thừa'],
    ['Quá hạn', `${nOver} người`, nOver ? s.err : s.okc, 'Quá hạn quyết toán · chặn ứng mới'],
  ] as const;
  const rec = SETTLE_REC.map(([k, name, gl, sub]) => ({ k, name, gl, sub, bad: gl !== sub }));
  const recBad = rec.filter((r) => r.bad).length;
  const cl = closeConditions();
  const clBad = cl.filter((c) => c[2] > 0).length;

  const detailBody = cur && cc ? (
      <>
        {cur.over > 0 && (
          <span className={s.overChip} title="Quá hạn quyết toán · chặn ứng mới">
            <IconLock size={13} aria-hidden="true" />
            {tx('stl.block')}
          </span>
        )}
        <div className={s.dwKpis}>
          {dwKpi.map(([k, v, tone]) => (
            <div key={k} className={s.dwKpi}>
              <div>{k}</div>
              <div className={tone}>{v}</div>
            </div>
          ))}
        </div>
        <div className={s.dwSlips}>
          {cur.slips.map((x, i) => (
            <div key={`${x.no}-${i}`} className={s.slip}>
              <div className={s.slipHead}>
                <b>{x.no}</b>
                <span className={s.slipDate}>
                  <IconCalendar size={13} aria-hidden="true" />
                  {x.date}
                </span>
                {jobTo(x.job) ? (
                  <Link to="/ezy/cost/$job" params={{ job: x.job }} className={s.slipJob}>
                    {x.job}
                  </Link>
                ) : (
                  <span className={s.slipJob}>{x.job}</span>
                )}
                <span style={{ flex: 1 }} />
                <span className={`${s.slipSt} ${SLIP_ST[x.state][1]}`}>{SLIP_ST[x.state][0]}</span>
                <b>{tr(x.amount)}</b>
              </div>
              {x.lines.map((l, j) => {
                const fee = SETTLE_FEES[l.fee]!;
                const code = fee.code || 'Mã chờ cấp';
                return (
                  <div key={j} className={s.line}>
                    <span className={s.lineSub}>{l.sub}</span>
                    <span className={`${s.pill} ${s.p_code}`} title={`${fee.code ? `Mã phí ${fee.code}` : 'Mã chờ cấp'} · ${fee.k}`}>{code}</span>
                    <span className={`${s.pill} ${s[`p_${l.nat}`]}`} title={`Bản chất ${l.nat} · ${NAT_TIP[l.nat]}`}>{l.nat}</span>
                    <span className={`${s.pill} ${s.p_EMP}`} title="Người trả EMP · Nhân viên trả">EMP</span>
                    <span className={`${s.pill} ${s[`p_${l.doc}`]}`} title={`Chứng từ ${l.doc} · ${DOC_TIP[l.doc]}`}>{l.doc}</span>
                    {l.rcv && <span className={s.rcv} title="Loại người nhận">{l.rcv === 'IND' ? 'Cá nhân' : 'Doanh nghiệp tư nhân'}</span>}
                    {l.nat === 'OBH' && x.state === 'ok' && <span className={s.rcv} title={tx('stl.refundNowTip')}>{tx('stl.refundNow')}</span>}
                    {lineFlags(l).map(([k, tip, lv]) => (
                      <span key={k} className={`${s.flag} ${lv === 'err' ? s.flag_err : s.flag_warn}`} title={tip} aria-label={tip}>
                        <IconAlertTriangle size={12} aria-hidden="true" />
                        {k}
                      </span>
                    ))}
                    <b className={s.lineAmt}>{l.amount.toLocaleString('vi-VN')}</b>
                  </div>
                );
              })}
            </div>
          ))}
          {cur.slips.length === 0 && <div className={s.none}>{tx('stl.noSlip')}</div>}
        </div>
      </>
  ) : null;
  const detailFooter = cur && cc ? (
        <>
          {cc.nWait > 0 && <span className={s.dwHint}>{tx('stl.waitN', { n: cc.nWait })}</span>}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" aria-label="Nhắc quyết toán" icon={<IconBell size={16} aria-hidden="true" />} onClick={noWrite} />
          <Button variant="primary" icon={<IconCheck size={16} aria-hidden="true" />} onClick={noWrite}>
            {tx('stl.netSettle')}
          </Button>
        </>
      ) : undefined;

  // iPad (Mint-Settle-iPad › page=detail): chi tiết người là trang riêng, có nút quay lại — không phải ngăn.
  if (pad && cur && cc) {
    return (
      <Page kind="table">
        <PageHeader
          title={cur.u}
          sub={cur.seat}
          findHint={false}
          lead={<Button variant="ghost" aria-label="Về danh sách" icon={<IconChevronLeft size={18} aria-hidden="true" />} onClick={() => setWho(null)} />}
        />
        <div className={q.dCard}>
          <div className={q.dBody}>{detailBody}</div>
          <div className={q.dFoot}>{detailFooter}</div>
        </div>
      </Page>
    );
  }

  return (
    <Page kind="table">
      <PageHeader
        title="Quyết toán tạm ứng"
        findHint={false}
        tabs={
          <>
          <div className={s.seg} role="tablist" aria-label="Màn quyết toán">
            {([['list', 'Quyết toán'], ['close', clBad ? `Khoá kỳ · ${clBad}` : 'Khoá kỳ']] as const).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={view === k} style={{ height: 30, fontSize: 13 }} onClick={() => setView(k)}>
                {l}
              </button>
            ))}
          </div>
          {nOver > 0 && (
            <span className={s.overChip} title="Quá hạn quyết toán · chặn ứng mới">
              <IconLock size={13} aria-hidden="true" />
              {tx('stl.overN', { n: nOver })}
            </span>
          )}
          </>
        }
        actions={
          <>
            <Link to="/ezy/cost" className={s.tool} aria-label="Chi phí · tab Tạm ứng" title="Chi phí · tab Tạm ứng">
              <IconExternalLink size={16} aria-hidden="true" />
            </Link>
            <Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Chưa mở xuất Excel ở bản này' })}>
              {tx('stl.export')}
            </Button>
          </>
        }
      />
      <div className={`${s.page} ${pad ? q.page : ''}`}>
        <StateView state={state} skeleton={<Skeleton rows={7} />}>
          {view === 'list' ? (
            <>
              <div className={s.bar}>
                <div className={s.seg} role="tablist" aria-label="Kỳ">
                  {([['m10', 'Tháng 10'], ['m09', 'Tháng 9']] as const).map(([k, l]) => (
                    <button key={k} type="button" role="tab" aria-selected={per === k} onClick={() => setPer(k)}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className={s.seg} role="tablist" aria-label="Lọc">
                  {FILTERS.map(([k, l]) => (
                    <button key={k} type="button" role="tab" aria-selected={fil === k} onClick={() => setFil(k)}>
                      {l}
                    </button>
                  ))}
                </div>
                <label className={s.sel}>
                  <IconFilter size={15} aria-hidden="true" />
                  <select aria-label="Bộ phận" value={grp} onChange={(e) => setGrp(e.target.value as SettleGroup | 'all')}>
                    {SETTLE_GROUPS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <span className={s.count}>
                  <IconChecklist size={14} aria-hidden="true" />
                  {tx('stl.people', { n: rows.length })}
                </span>
              </div>
              <div className={s.kpis}>
                {kpis.map(([k, v, tone, tip]) => (
                  <div key={k} className={s.kpi} title={tip}>
                    <div>{k}</div>
                    <div className={tone}>{v}</div>
                  </div>
                ))}
              </div>
              <div className={`${s.cols} ${pad ? q.cols : ''}`}>
                {pad ? (
                  <div className={q.cards} aria-label="Quyết toán theo người">
                    {rows.map((p) => {
                      const c = settleCalc(p);
                      return (
                        <button key={p.u} type="button" className={q.card} aria-label={`Xem quyết toán · ${p.u}`} onClick={() => setWho(p.u)}>
                          <span className={q.top}>
                            <Avatar u={p.u} photo={p.photo} cls={s.av} />
                            <span className={q.ttl}>
                              <b>
                                {p.u}
                                {c.nRej > 0 && (
                                  <span className={s.rej} title={`${c.nRej} phiếu bị từ chối · ${tr(c.rej)} vẫn tính đang giữ`} aria-label={`${c.nRej} phiếu bị từ chối`}>
                                    ✕ {c.nRej}
                                  </span>
                                )}
                              </b>
                              <span>{p.seat}</span>
                            </span>
                            {p.over > 0 && (
                              <span className={q.lock} title="Chặn ứng mới · quá hạn quyết toán" aria-label="Chặn ứng mới">
                                <IconLock size={16} aria-hidden="true" />
                              </span>
                            )}
                          </span>
                          <span className={q.stats}>
                            <span><span>{tx('stl.holding')}</span><b>{tr(p.hold)}</b></span>
                            <span><span>{tx('stl.empWait')}</span><b className={c.nWait ? s.warn : ''}>{c.nWait ? `${c.nWait} · ${tr(c.wait)}` : '—'}</b></span>
                            <span><span>{tx('stl.backRet')}</span><b className={c.back ? s.link : c.ret ? s.warn : s.okc}>{c.back ? `Hoàn ${tr(c.back)}` : c.ret ? `Nộp ${tr(c.ret)}` : 'Cân'}</b></span>
                            <span><span>{tx('stl.due')}</span><b className={p.over ? s.err : ''}>{p.over ? `−${p.over} ngày` : p.due}</b></span>
                          </span>
                        </button>
                      );
                    })}
                    {rows.length === 0 && <div className={s.none}>{tx('stl.nobody')}</div>}
                  </div>
                ) : (
                <section className={`${s.card} ${s.list}`} aria-label="Quyết toán theo người">
                  <div className={s.head} aria-hidden="true">
                    <span>{tx('stl.person')}</span>
                    <span className={s.r}>{tx('stl.holding')}</span>
                    <span className={s.r}>{tx('stl.empWait')}</span>
                    <span className={s.r}>{tx('stl.net')}</span>
                    <span className={s.r}>{tx('stl.backRet')}</span>
                    <span className={s.r}>{tx('stl.due')}</span>
                    <span className={s.c}>{tx('stl.blockCol')}</span>
                  </div>
                  {rows.map((p) => {
                    const c = settleCalc(p);
                    return (
                      <button key={p.u} type="button" className={s.row} aria-label={`Xem quyết toán · ${p.u}`} onClick={() => setWho(p.u)}>
                        <span className={s.who}>
                          <Avatar u={p.u} photo={p.photo} cls={s.av} />
                          <span style={{ minWidth: 0 }}>
                            <b className={s.name}>
                              {p.u}
                              {c.nRej > 0 && (
                                <span className={s.rej} title={`${c.nRej} phiếu bị từ chối · ${tr(c.rej)} vẫn tính đang giữ`} aria-label={`${c.nRej} phiếu bị từ chối`}>
                                  ✕ {c.nRej}
                                </span>
                              )}
                            </b>
                            <span className={s.seat}>{p.seat}</span>
                          </span>
                        </span>
                        <span className={s.r}>{tr(p.hold)}</span>
                        <span className={`${s.r} ${c.nWait ? s.warn : ''}`} title="Phiếu EMP chờ duyệt">{c.nWait ? `${c.nWait} · ${tr(c.wait)}` : '—'}</span>
                        <span className={s.r}>{tr(c.net)}</span>
                        <b className={`${s.r} ${s.b} ${c.back ? s.link : c.ret ? s.warn : s.okc}`} title={c.back ? 'Công ty hoàn ứng cho nhân viên' : c.ret ? 'Nhân viên nộp lại phần thừa' : 'Đã cân'}>
                          {c.back ? `Hoàn ${tr(c.back)}` : c.ret ? `Nộp ${tr(c.ret)}` : 'Cân'}
                        </b>
                        <span className={`${s.due} ${p.over ? s.err : s.tx2}`} title={p.over ? `Quá hạn quyết toán ${p.over} ngày làm việc · hạn ${p.due}` : `Hạn quyết toán ${p.due}`}>
                          <IconStopwatch size={13} aria-hidden="true" />
                          {p.over ? `−${p.over} ngày` : p.due}
                        </span>
                        <span className={s.c}>
                          {p.over > 0 && (
                            <span className={s.block} title="Chặn ứng mới · quá hạn quyết toán" aria-label="Chặn ứng mới">
                              <IconLock size={14} aria-hidden="true" />
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {rows.length === 0 && <div className={s.none}>{tx('stl.nobody')}</div>}
                </section>
                )}
                <section className={`${s.card} ${s.rec} ${pad ? q.rec : ''}`} aria-label="Đối soát trước khoá kỳ">
                  <div className={s.rh}>
                    <IconUserCheck size={16} aria-hidden="true" />
                    <h2>{tx('stl.recTitle')}</h2>
                    <span className={`${s.chip} ${recBad ? s.chip_bad : s.chip_ok}`}>{recBad ? `${recBad} TK lệch` : 'Khớp'}</span>
                  </div>
                  <div className={s.rg}>
                    <span>TK</span>
                    <span>{tx('stl.ledger')}</span>
                    <span style={{ textAlign: 'right' }}>{tx('stl.diff')}</span>
                  </div>
                  {rec.map((r) => (
                    <div key={r.k} className={s.rr} title={`TK ${r.k} · ${r.name}`}>
                      <b>{r.k}</b>
                      <span>
                        <span className={s.m}>{r.name}</span>
                        <span className={s.s}>{tr(r.gl)} · {tr(r.sub)}</span>
                      </span>
                      <span className={s.dl}>
                        <b className={r.bad ? s.err : s.okc}>{r.bad ? tr(r.gl - r.sub) : 'Khớp'}</b>
                        {r.bad && (
                          <button type="button" className={s.fix} aria-label={`Sinh bút toán đối ứng TK ${r.k}`} title="Sinh bút toán đối ứng" onClick={noWrite}>
                            <IconPencil size={14} aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                  <button type="button" className={s.lock} title="Khoá kỳ · điều kiện + đối soát" onClick={() => setView('close')}>
                    <IconLock size={16} aria-hidden="true" />
        {tx('stl.lockPeriod')}
                  </button>
                </section>
              </div>
            </>
          ) : (
            <section className={`${s.card} ${s.cl}`} aria-label="Điều kiện khoá kỳ">
              <div className={s.rh}>
                <IconLockOpen size={16} aria-hidden="true" />
                <h2>{tx('stl.clTitle')}</h2>
                <span className={`${s.chip} ${clBad ? s.chip_bad : s.chip_ok}`}>{clBad ? `${clBad} mục chặn` : 'Đủ điều kiện'}</span>
              </div>
              {cl.map(([k, name, n, unit, src]) => (
                <div key={k} className={s.cr} title={`${name} · ${src}`}>
                  <span className={`${s.ic} ${n ? s.i_bad : s.i_ok}`} aria-hidden="true">
                    {n ? <IconAlertTriangle size={15} /> : <IconCheck size={15} />}
                  </span>
                  <span className={s.tx}>
                    <b>{name}</b>
                    <span>{src}</span>
                  </span>
                  <b className={`${s.n} ${n ? s.err : s.okc}`}>{n ? `${n} ${unit}` : tx('stl.done')}</b>
                </div>
              ))}
            </section>
          )}
        </StateView>
      </div>
      <Drawer
        open={!!cur && !pad}
        onOpenChange={(o) => !o && setWho(null)}
        title={cur ? `Quyết toán ${cur.u}` : ''}
        description={cur?.seat}
        footer={detailFooter}
      >
        {detailBody}
      </Drawer>
    </Page>
  );
}
